// Live updates: long-polls GET /v1/changes through the
// app's own Worker and hands each batch of changes to the app, which refetches
// what they name. The first call only fetches the head cursor; the page loads
// its state with the normal GETs.
import { useEffect, useRef, useState } from 'react';
import { API_BASE } from './api/client.ts';
import type { Change, ChangesPage } from './api/types.ts';

export type LiveStatus = 'connecting' | 'live' | 'retrying' | 'off';

export function useLiveChanges({
  onChanges,
  onReset,
  headers,
  enabled = true,
}: {
  onChanges: (changes: Change[]) => void;
  /** The cursor expired (410): refetch everything. */
  onReset: () => void;
  headers?: () => Record<string, string>;
  enabled?: boolean;
}): LiveStatus {
  const [status, setStatus] = useState<LiveStatus>('connecting');
  const latest = useRef({ onChanges, onReset, headers });
  latest.current = { onChanges, onReset, headers };

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let ctrl: AbortController | undefined;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    (async () => {
      let cursor: string | null = null;
      let backoff = 1000;
      while (!stopped) {
        try {
          ctrl = new AbortController();
          const qs = cursor ? `?after=${encodeURIComponent(cursor)}&wait=20` : '';
          const res = await fetch(`${API_BASE}/changes${qs}`, { headers: latest.current.headers?.(), credentials: 'same-origin', signal: ctrl.signal });
          // 410: older than retention; 400: a cursor the server doesn't know. Start again.
          if (res.status === 410 || res.status === 400) {
            cursor = null;
            latest.current.onReset();
            continue;
          }
          // Not signed in, or an API without the feed yet: stop rather than hammer it.
          if (res.status === 401 || res.status === 403 || res.status === 404) {
            setStatus('off');
            return;
          }
          if (!res.ok) throw new Error(`changes ${res.status}`);
          const page = (await res.json()) as ChangesPage;
          if (cursor !== null && page.changes.length) latest.current.onChanges(page.changes);
          cursor = page.cursor;
          backoff = 1000;
          setStatus('live');
        } catch {
          if (stopped) return;
          setStatus('retrying');
          await sleep(backoff);
          backoff = Math.min(backoff * 2, 15_000);
        }
      }
    })();

    return () => {
      stopped = true;
      ctrl?.abort();
    };
  }, [enabled]);

  return enabled ? status : 'off';
}

/** A small "Live" indicator for headers. */
export function liveLabel(status: LiveStatus): { text: string; tone: 'ok' | 'warn' | 'neutral' } {
  if (status === 'live') return { text: 'Live', tone: 'ok' };
  if (status === 'retrying') return { text: 'Reconnecting', tone: 'warn' };
  if (status === 'connecting') return { text: 'Connecting', tone: 'neutral' };
  return { text: 'Not live', tone: 'neutral' };
}

/** True for something created in the last `ms` (for briefly highlighting new rows). */
export function isFresh(iso: string | null | undefined, ms = 120_000): boolean {
  return !!iso && Date.now() - Date.parse(iso) < ms;
}
